// src/pages/Auth/NewPasswordModal.tsx

import React, { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const FiEyeComponent = FiEye as unknown as React.ComponentType<{
    size?: number | string;
}>;
const FiEyeOffComponent = FiEyeOff as unknown as React.ComponentType<{
    size?: number | string;
}>;

interface NewPasswordModalProps {
    onSubmit: (password: string) => void;
    onClose: () => void;
    loading?: boolean;
}

const NewPasswordModal: React.FC<NewPasswordModalProps> = ({
    onSubmit,
    onClose,
    loading = false,
}) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});

    const validatePassword = (value: string) => {
        if (!value) {
            setErrors(prev => ({ ...prev, password: 'Password is required' }));
            return false;
        }
        if (value.length < 8) {
            setErrors(prev => ({ ...prev, password: '8 character minimum' }));
            return false;
        }
        if (!/[a-z]/.test(value)) {
            setErrors(prev => ({ ...prev, password: 'One lowercase character' }));
            return false;
        }
        if (!/[A-Z]/.test(value)) {
            setErrors(prev => ({ ...prev, password: 'One uppercase character' }));
            return false;
        }
        if (!/[\d\W]/.test(value)) {
            setErrors(prev => ({ ...prev, password: 'One number or special symbol' }));
            return false;
        }
        setErrors(prev => ({ ...prev, password: undefined }));
        return true;
    };

    const validateConfirmPassword = (value: string) => {
        if (!value) {
            setErrors(prev => ({ ...prev, confirmPassword: 'Please confirm your password' }));
            return false;
        }
        if (value !== password) {
            setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
            return false;
        }
        setErrors(prev => ({ ...prev, confirmPassword: undefined }));
        return true;
    };

    const handlePasswordChange = (value: string) => {
        setPassword(value);
        validatePassword(value);
        if (confirmPassword) {
            validateConfirmPassword(confirmPassword);
        }
    };

    const handleConfirmChange = (value: string) => {
        setConfirmPassword(value);
        validateConfirmPassword(value);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const isPasswordValid = validatePassword(password);
        const isConfirmValid = validateConfirmPassword(confirmPassword);

        if (isPasswordValid && isConfirmValid) {
            onSubmit(password);
        }
    };

    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecialOrDigit = /[\d\W]/.test(password);
    const minLength = password.length >= 8;
    const passwordsMatch = password === confirmPassword && password !== '';
    const isFormValid = password && confirmPassword && hasLowercase && hasUppercase && hasSpecialOrDigit && minLength && passwordsMatch;

    return (
        <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50'>
            <div
                className='bg-white rounded-lg shadow-lg max-w-sm w-full'
                style={{
                    padding: '20px',
                }}
            >
                <h3
                    className='text-lg font-semibold mb-1'
                    style={{
                        color: '#3F5265',
                        fontSize: '16px',
                    }}
                >
                    Set a new password
                </h3>
                <p
                    className='text-sm text-gray-600 mb-6'
                    style={{
                        color: '#657584',
                        fontSize: '12px',
                    }}
                >
                    Choose a new password you haven't used before.
                </p>

                <form onSubmit={handleSubmit}>
                    <div
                        style={{
                            paddingLeft: '20px',
                            paddingRight: '20px',
                            paddingBottom: '0px',
                        }}
                    >
                        <div>
                            <label
                                className='block text-sm'
                                style={{ color: '#9FA9B2', fontSize: '10px' }}
                            >
                                NEW PASSWORD
                                <span className='text-red-500'>*</span>
                            </label>
                            <div className='relative'>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => handlePasswordChange(e.target.value)}
                                    placeholder='Set new password'
                                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.password ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    disabled={loading}
                                />
                                <button
                                    type='button'
                                    onClick={() => setShowPassword(!showPassword)}
                                    className='absolute inset-y-0 right-3 flex items-center text-black-400'
                                >
                                    {showPassword ? (
                                        <FiEyeComponent size={18} />
                                    ) : (
                                        <FiEyeOffComponent size={18} />
                                    )}
                                </button>
                            </div>

                            <ul
                                className='text-sm mt-1'
                                style={{
                                    lineHeight: 1.5,
                                    paddingLeft: '5px',
                                }}
                            >
                                <li
                                    style={{
                                        color: hasLowercase ? 'green' : 'orange',
                                        fontSize: '10px',
                                    }}
                                >
                                    • One lowercase character
                                </li>
                                <li
                                    style={{
                                        color: hasUppercase ? 'green' : 'orange',
                                        fontSize: '10px',
                                    }}
                                >
                                    • One uppercase character
                                </li>
                                <li
                                    style={{
                                        color: hasSpecialOrDigit ? 'green' : 'orange',
                                        fontSize: '10px',
                                    }}
                                >
                                    • One number or special symbol
                                </li>
                                <li
                                    style={{
                                        color: minLength ? 'green' : 'orange',
                                        fontSize: '10px',
                                    }}
                                >
                                    • 8 character minimum
                                </li>
                            </ul>
                            {errors.password && (
                                <p className='text-red-500 text-xs mt-1'>{errors.password}</p>
                            )}
                        </div>

                        <div>
                            <label
                                className='block text-sm mt-3'
                                style={{ color: '#9FA9B2', fontSize: '10px' }}
                            >
                                CONFIRM NEW PASSWORD
                                <span className='text-red-500'>*</span>
                            </label>
                            <div className='relative'>
                                <input
                                    type={showConfirm ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => handleConfirmChange(e.target.value)}
                                    placeholder='Re-enter new password'
                                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    disabled={loading}
                                />
                                <button
                                    type='button'
                                    onClick={() => setShowConfirm(!showConfirm)}
                                    className='absolute inset-y-0 right-3 flex items-center text-black-400'
                                >
                                    {showConfirm ? (
                                        <FiEyeComponent size={18} />
                                    ) : (
                                        <FiEyeOffComponent size={18} />
                                    )}
                                </button>
                            </div>
                            {confirmPassword && (
                                <p
                                    className='mt-1'
                                    style={{
                                        color: passwordsMatch ? 'green' : 'red',
                                        fontSize: '10px',
                                        paddingLeft: '10px',
                                    }}
                                >
                                    {passwordsMatch ? 'Password Matched' : 'Passwords do not match'}
                                </p>
                            )}
                            {errors.confirmPassword && (
                                <p className='text-red-500 text-xs mt-1'>{errors.confirmPassword}</p>
                            )}
                        </div>
                    </div>

                    <div className='flex gap-3 justify-end mt-4'>
                        <button
                            type='button'
                            onClick={onClose}
                            className='px-3 py-1 border border-gray-300 rounded-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors'
                            disabled={loading}
                            style={{
                                fontSize: '12px',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            disabled={!isFormValid || loading}
                            className={`px-3 py-1 rounded-sm text-sm font-medium text-white ${isFormValid && !loading
                                    ? 'bg-orange-500 hover:bg-orange-600'
                                    : 'bg-gray-300 cursor-not-allowed'
                                }`}
                            style={{
                                fontSize: '12px',
                            }}
                        >
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewPasswordModal;